import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, AlertCircle, Filter, FileText } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import InvoiceForm from '../invoices/InvoiceForm';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const STATUSES = ['pending','processing','completed','cancelled'];

function authHeaders(json = true) {
  const token = localStorage.getItem('auth_token');
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

function getStatusBadge(status) {
  switch (status) {
    case 'pending':    return 'bg-yellow-100 text-yellow-800';
    case 'processing': return 'bg-blue-100 text-blue-800';
    case 'completed':  return 'bg-green-100 text-green-800';
    case 'cancelled':  return 'bg-red-100 text-red-800';
    default:           return 'bg-gray-100 text-gray-800';
  }
}

const inp = 'w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500';

export default function AdminInvoices() {
  const { t } = useLanguage();
  const [rows, setRows]         = useState([]);
  const [users, setUsers]       = useState([]);
  const [selUser, setSelUser]   = useState('');
  const [search, setSearch]     = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusF, setStatusF]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [editRateId, setEditRateId] = useState(null);
  const [rateInput, setRateInput]   = useState('');
  const [savingRate, setSavingRate] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/users/`, { headers: authHeaders(false) });
      if (res.ok) setUsers(await res.json());
    } catch {}
  }, []);

  const fetchData = useCallback(async (uid, q, st) => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams();
      if (uid) p.set('created_by', uid);
      if (q)   p.set('company', q);
      if (st)  p.set('status', st);
      const res = await fetch(`${BASE_URL}/invoices/?${p}`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRows(Array.isArray(data) ? data : (data.invoices || data.results || []));
      setCurrentPage(1);
    } catch { setError(t('fetchInvoicesError') || 'Failed to load.'); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchData(selUser, '', ''); }, [selUser, fetchData]);

  const openView = async (r) => {
    try {
      const res = await fetch(`${BASE_URL}/invoices/${r.id}/`, { headers: authHeaders(false) });
      setViewInvoice(res.ok ? await res.json() : r);
    } catch { setViewInvoice(r); }
  };

  const startEditRate = (r) => { setEditRateId(r.id); setRateInput(String(Number(r.exchange_rate) || '')); setError(''); };
  const cancelEditRate = () => { setEditRateId(null); setRateInput(''); };

  const saveRate = async (r) => {
    const val = parseFloat(rateInput);
    if (!(val > 0)) { setError('Exchange rate must be greater than 0.'); return; }
    setSavingRate(true);
    try {
      const res = await fetch(`${BASE_URL}/invoices/${r.id}/`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ exchange_rate: val }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update rate.');
      // Update the row in place (rate + recomputed totals).
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, ...data } : x));
      cancelEditRate();
    } catch (e) { setError(e.message); }
    finally { setSavingRate(false); }
  };

  const totalPages = Math.ceil(rows.length / pageSize);
  const paged = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="container mx-auto space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{t('invoices')}</h1>
        <div className="flex flex-wrap gap-1 sm:gap-2">
          <button onClick={() => setFilterOpen(o => !o)}
            className={`px-3 py-2 rounded-md flex items-center text-sm ${filterOpen ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <Filter className="h-4 w-4 mr-1" /> {t('filters')}
          </button>
          <button onClick={() => fetchData(selUser, search, statusF)} disabled={loading}
            className="px-3 py-2 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> {t('refresh')}
          </button>
        </div>
      </div>

      {filterOpen && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <form onSubmit={e => { e.preventDefault(); fetchData(selUser, search, statusF); }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
              <select value={selUser} onChange={e => setSelUser(e.target.value)} className={inp}>
                <option value="">{t('all')}</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('company')}</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('company')}
                  className="pl-8 w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('status')}</label>
              <select value={statusF} onChange={e => setStatusF(e.target.value)} className={inp}>
                <option value="">{t('all')}</option>
                {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button type="submit" className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center justify-center text-sm">
                <Search className="h-4 w-4 mr-1" /> {t('search')}
              </button>
              <button type="button" onClick={() => { setSearch(''); setStatusF(''); setSelUser(''); fetchData('', '', ''); }}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 text-sm">
                {t('reset')}
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-800">{t('invoices')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('invoiceNumber')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('company')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('date')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('serviceType')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('totalAmountGEL')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount AMD + VAT</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exchange rate</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('status')}</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('paymentStatus')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center">
                  <div className="flex justify-center items-center">
                    <RefreshCw className="animate-spin h-5 w-5 text-primary-600 mr-2" />
                    <span className="text-sm text-gray-500">{t('loading')}</span>
                  </div>
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm">{t('noInvoicesFound')}</p>
                </td></tr>
              ) : paged.map(r => (
                <tr key={r.id} onClick={() => openView(r)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-3 py-3 whitespace-nowrap text-primary-600 font-medium">{r.invoice_number}</td>
                  <td className="px-3 py-3 text-gray-800 max-w-[140px] truncate">{r.company}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-gray-500 text-xs">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-gray-600">{t(r.service_type)}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-medium text-gray-900">
                    {r.total_amountGel != null ? new Intl.NumberFormat().format(Number(r.total_amountGel).toFixed(2)) : '—'}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap font-medium text-gray-900">
                    {r.tts_georgia
                      ? '—'
                      : (r.total_amount != null ? new Intl.NumberFormat().format(Number(r.total_amount).toFixed(2)) : '—')}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-gray-600" onClick={e => e.stopPropagation()}>
                    {(r.tts_georgia && r.service_type !== 'russiaInsurance') ? (
                      '—'
                    ) : r.status === 'pending' ? (
                      editRateId === r.id ? (
                        <input
                          type="number" step="0.0001" min="0" autoFocus value={rateInput}
                          disabled={savingRate}
                          onChange={e => setRateInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveRate(r); if (e.key === 'Escape') cancelEditRate(); }}
                          onBlur={() => saveRate(r)}
                          className="w-24 px-2 py-1 border border-primary-400 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      ) : (
                        <button type="button" onClick={() => startEditRate(r)}
                          title="Click to edit (pending only)"
                          className="text-primary-600 hover:text-primary-800 underline decoration-dotted">
                          {r.exchange_rate != null ? Number(r.exchange_rate) : '—'} ✎
                        </button>
                      )
                    ) : (
                      r.exchange_rate != null ? Number(r.exchange_rate) : '—'
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(r.status)}`}>{t(r.status)}</span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${r.paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {r.paid ? t('paid') : t('unpaid')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, rows.length)} / {rows.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">{rows.length} record{rows.length !== 1 ? 's' : ''}</div>
        )}
      </div>

      <InvoiceForm
        isOpen={!!viewInvoice}
        onClose={() => setViewInvoice(null)}
        onSubmit={() => {}}
        invoice={viewInvoice}
        title={t('viewInvoice')}
        isViewMode={true}
        isLoading={false}
        showNotification={() => {}}
      />
    </div>
  );
}
